import { useEffect, useRef, useState } from "preact/hooks";
import { ChatButton } from "./components/ChatButton";
import { ChatWindow } from "./components/ChatWindow";
import { PreChatForm } from "./components/PreChatForm";
import { MessageList } from "./components/MessageList";
import { MessageInput } from "./components/MessageInput";
import { bootstrapSession, BootstrapError } from "./lib/api";
import { trackEvent } from "./lib/analytics";
import { ChatWS } from "./lib/ws";
import {
  clearSession,
  getOrCreateVisitorId,
  loadSession,
  loadVisitorInfo,
  markNudgeShown,
  saveSession,
  saveVisitorInfo,
  wasNudgeShown,
} from "./lib/storage";
import type { PoweredBy, Theme, UIMessage, VisitorInfo } from "./types";
import { uuidv4 } from "./lib/uuid";

interface ChatWidgetProps {
  siteKey: string;
  agentUrl: string;
  initialTheme?: Theme;
}

type Phase = "closed" | "form" | "connecting" | "ready" | "ended";

// Cap the resume window slightly below the server's 24h token TTL so
// a stored session never out-survives its token.
const RESUME_WINDOW_MS = 23 * 60 * 60 * 1000;

// Launcher attention nudge timing. Fire the wiggle a few seconds after
// load — late enough that it lands once the page has settled and the
// visitor's eye is free, not competing with first paint. Then hold the
// CTA tooltip briefly before it auto-dismisses. Both are easy to tune.
const NUDGE_DELAY_MS = 3500;
const NUDGE_VISIBLE_MS = 6000;

export function ChatWidget({
  siteKey,
  agentUrl,
  initialTheme,
}: ChatWidgetProps) {
  const [phase, setPhase] = useState<Phase>("closed");
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [pendingAssistant, setPendingAssistant] = useState("");
  const [toolActivity, setToolActivity] = useState<string | null>(null);
  // Theme: install-code wins. Initial value comes from data-theme on
  // the script tag (read in widget.tsx). The server-provided theme
  // from the bootstrap response is only applied if no install-code
  // theme was given — backward compat for older embeds.
  const installThemeProvided =
    !!initialTheme && Object.keys(initialTheme).length > 0;
  const [theme, setTheme] = useState<Theme>(initialTheme || {});
  const [formError, setFormError] = useState<string | null>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  // Whether the launcher is currently showing its one-time attention
  // nudge (wiggle + optional CTA tooltip). Driven by the timers below.
  const [showNudge, setShowNudge] = useState(false);

  const wsRef = useRef<ChatWS | null>(null);
  // Messages the user submitted while the WS was not OPEN. They sit
  // here until the next successful `ready` frame, then get flushed.
  const pendingOutboxRef = useRef<string[]>([]);
  // Guards against starting a second reconnect while one is in flight.
  const reconnectingRef = useRef(false);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, []);

  // Apply theme tokens as CSS custom properties on the host element so
  // the shadow-DOM stylesheet can read them. Theme arrives from the
  // bootstrap response (chat_widgets.theme jsonb).
  useEffect(() => {
    const host = document.querySelector(
      "nejix-chat-host",
    ) as HTMLElement | null;
    if (!host) return;
    if (theme.primary) host.style.setProperty("--nx-primary", theme.primary);
    if (theme.primary_text)
      host.style.setProperty("--nx-primary-text", theme.primary_text);
    if (theme.background)
      host.style.setProperty("--nx-background", theme.background);
    if (theme.text) host.style.setProperty("--nx-text", theme.text);
    if (theme.border) host.style.setProperty("--nx-border", theme.border);
    if (theme.offset_bottom)
      host.style.setProperty("--nx-offset-bottom", theme.offset_bottom);
    if (theme.offset_side)
      host.style.setProperty("--nx-offset-side", theme.offset_side);
    if (theme.position === "left") host.dataset.position = "left";
    else host.dataset.position = "right";
  }, [theme]);

  // Schedule the one-time attention nudge. Only when the customer enabled
  // it, only while the launcher is closed, and only once per session.
  // Returning to "closed" before it ever fires restarts the delay; once
  // it has fired (and marked the session), it won't fire again.
  useEffect(() => {
    if (!theme.wiggle) return;
    if (phase !== "closed") return;
    if (wasNudgeShown(siteKey)) return;
    const timer = window.setTimeout(() => {
      setShowNudge(true);
      markNudgeShown(siteKey);
    }, NUDGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [theme.wiggle, phase, siteKey]);

  // Auto-dismiss the nudge after its visible window. Opening the chat or
  // hitting the tooltip's close button clears it sooner.
  useEffect(() => {
    if (!showNudge) return;
    const timer = window.setTimeout(() => setShowNudge(false), NUDGE_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [showNudge]);

  const handleOpen = () => {
    setShowNudge(false);
    const saved = loadSession(siteKey);
    if (saved) {
      attachWebSocket(saved.ws_url, /* resuming */ true);
      setPhase("connecting");
      return;
    }
    setRuntimeError(null);
    setFormError(null);
    setPhase("form");
  };

  const handleClose = () => {
    // Only minimize — don't tear down the WS. The agent's grace
    // window keeps the session alive for 90s if the visitor reopens.
    setPhase("closed");
  };

  const handleSubmitForm = async (info: VisitorInfo) => {
    saveVisitorInfo(info);
    setFormError(null);
    setPhase("connecting");
    try {
      const response = await bootstrapSession(agentUrl, {
        site_key: siteKey,
        visitor: { ...info, id: getOrCreateVisitorId() },
        origin: window.location.origin,
      });
      saveSession(siteKey, {
        session_id: response.session_id,
        token: response.token,
        ws_url: response.ws_url,
        expires_at: Date.now() + RESUME_WINDOW_MS,
      });
      // Only fall back to the server theme if the install code didn't
      // include one. Keeps the install-time render consistent with the
      // post-bootstrap state when the customer specified a theme.
      if (!installThemeProvided) {
        setTheme(response.theme || {});
      }
      attachWebSocket(response.ws_url, /* resuming */ false);
    } catch (err) {
      const message =
        err instanceof BootstrapError
          ? err.message
          : "Unable to start chat. Please try again.";
      setFormError(message);
      setPhase("form");
    }
  };

  const attachWebSocket = (wsUrl: string, resuming: boolean) => {
    wsRef.current?.close();

    // Track whether the connection ever made it to a ready frame.
    // A disconnect or timeout BEFORE ready means the session is dead
    // (resume) or the agent rejected the upgrade (fresh) — both need
    // to fall back to the form rather than hang in "connecting".
    // A disconnect AFTER ready triggers a silent reconnect via the
    // stored token; the agent's 90s grace window keeps the session
    // alive long enough for that to succeed in the common case.
    let resolved = false;
    let openTimer: number | null = window.setTimeout(() => {
      handleResumeFailure("Connection timed out");
    }, 5000);

    const clearOpenTimer = () => {
      if (openTimer !== null) {
        clearTimeout(openTimer);
        openTimer = null;
      }
    };

    const handleResumeFailure = (reason: string) => {
      if (resolved) return;
      resolved = true;
      clearOpenTimer();
      reconnectingRef.current = false;
      // The queued messages can't be delivered to a dead session.
      pendingOutboxRef.current = [];
      clearSession(siteKey);
      wsRef.current?.close();
      wsRef.current = null;
      setPendingAssistant("");
      setToolActivity(null);
      setMessages([]);
      setFormError(
        resuming
          ? "Your previous chat session has expired. Please start a new one."
          : `Couldn't connect to chat: ${reason}.`,
      );
      setPhase("form");
    };

    const ws = new ChatWS(wsUrl, {
      onReady: ({ messages: history }) => {
        resolved = true;
        clearOpenTimer();
        reconnectingRef.current = false;
        if (resuming) {
          // Rehydrate visible transcript from server-side history,
          // then append any messages the visitor queued while the
          // socket was down so they stay visible across the drop.
          const ui: UIMessage[] = [];
          for (const m of history) {
            if (m.role === "user") {
              ui.push({
                id: `h_${m.sequence}`,
                role: "user",
                content: m.content,
              });
            } else if (m.role === "assistant" && m.content) {
              ui.push({
                id: `h_${m.sequence}`,
                role: "assistant",
                content: m.content,
              });
            }
          }
          for (const content of pendingOutboxRef.current) {
            ui.push({ id: uuidv4(), role: "user", content });
          }
          setMessages(ui);
          // Drop any partial assistant stream that was in flight when
          // the socket closed — its final state (if any) is in history.
          setPendingAssistant("");
          setToolActivity(null);
        }
        // Flush queued outbound messages now that the socket is live.
        const outbox = pendingOutboxRef.current;
        pendingOutboxRef.current = [];
        for (const content of outbox) {
          ws.send({ type: "message", content });
        }
        setPhase("ready");
        setRuntimeError(null);
        if (!resuming) {
          trackEvent("ai_chat_start", { site_key: siteKey });
        }
      },
      onToken: (delta) => {
        setPendingAssistant((prev) => prev + delta);
      },
      onMessage: (content) => {
        setMessages((prev) => [
          ...prev,
          { id: uuidv4(), role: "assistant", content },
        ]);
        setPendingAssistant("");
        setToolActivity(null);
      },
      onToolCall: (name, status) => {
        setToolActivity(status === "running" ? name : null);
      },
      onError: (_code, message) => {
        setRuntimeError(message);
      },
      onEnded: (_reason) => {
        clearSession(siteKey);
        setPhase("ended");
      },
      onReplaced: () => {
        // Same session opened in another tab. Stay idle here; sending a
        // message reconnects and takes the session back (handleSend).
        wsRef.current = null;
        setPendingAssistant("");
        setToolActivity(null);
        setRuntimeError("This chat continued in another tab or window.");
      },
      onDisconnect: () => {
        if (!resolved) {
          // Never got ready — session is dead (404 from agent, expired
          // token, agent rejected). Fall back to the form.
          handleResumeFailure("Disconnected before ready");
          return;
        }
        // Post-ready drop on an established session. The agent's 90s
        // grace window keeps the session alive in Redis, so reattach
        // silently using the stored token. Anything the visitor types
        // before the new socket is ready will queue in the outbox and
        // flush on the next `ready` frame.
        reconnectIfNeeded();
      },
    });
    ws.connect();
    wsRef.current = ws;
  };

  const reconnectIfNeeded = () => {
    if (reconnectingRef.current) return;
    const saved = loadSession(siteKey);
    if (!saved) {
      setRuntimeError(
        "Connection lost. Please refresh the page to start a new chat.",
      );
      return;
    }
    reconnectingRef.current = true;
    attachWebSocket(saved.ws_url, /* resuming */ true);
  };

  const handleSend = (content: string) => {
    setMessages((prev) => [...prev, { id: uuidv4(), role: "user", content }]);
    const sent = wsRef.current?.send({ type: "message", content }) ?? false;
    if (!sent) {
      // Socket isn't OPEN — queue for delivery once we reconnect.
      // The optimistic UI message above is preserved by onReady
      // when it re-renders the transcript from history + outbox.
      pendingOutboxRef.current.push(content);
      reconnectIfNeeded();
    }
  };

  const title = theme.title || "Chat with us";
  const greeting = theme.greeting;
  // Brand the footer from the install-time theme (baked by the dashboard),
  // defaulting to the platform. Known on first render — no session needed.
  const poweredBy: PoweredBy = theme.powered_by ?? {
    name: "nejix",
    url: "https://nejix.com",
  };

  if (phase === "closed") {
    return (
      <ChatButton
        onClick={handleOpen}
        label={title}
        nudge={showNudge}
        cta={theme.cta}
        onDismissCta={() => setShowNudge(false)}
      />
    );
  }

  return (
    <ChatWindow
      title={title}
      greeting={greeting}
      poweredBy={poweredBy}
      onClose={handleClose}
    >
      {phase === "form" && (
        <PreChatForm
          initial={loadVisitorInfo() || undefined}
          onSubmit={handleSubmitForm}
          error={formError}
        />
      )}
      {phase === "connecting" && <div class="nx-loading">Connecting…</div>}
      {(phase === "ready" || phase === "ended") && (
        <>
          <MessageList
            messages={messages}
            pendingAssistant={pendingAssistant}
            toolActivity={toolActivity}
          />
          {phase === "ready" ? (
            <MessageInput onSend={handleSend} />
          ) : (
            <div class="nx-ended">
              This chat has ended. Refresh to start a new one.
            </div>
          )}
        </>
      )}
      {runtimeError && phase !== "form" && (
        <div class="nx-runtime-error">{runtimeError}</div>
      )}
    </ChatWindow>
  );
}
